// Presentation only: CMS HTML must be sanitized upstream, as for the existing
// v-html renderer. This helper is not an HTML sanitizer.
export function articleText(value = '') {
  return String(value)
    .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;|&#160;|&#x0*a0;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}
const escapeAttribute = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
const decodeAttribute = (value) =>
  String(value).replace(
    /&(amp|quot|apos|lt|gt|nbsp|#\d+|#x[\da-f]+);/gi,
    (match, code) => {
      const named = {
        amp: '&',
        quot: '"',
        apos: "'",
        lt: '<',
        gt: '>',
        nbsp: '\u00a0'
      };
      if (!code.startsWith('#')) return named[code.toLowerCase()] || match;
      const point = code.toLowerCase().startsWith('#x')
        ? parseInt(code.slice(2), 16)
        : Number(code.slice(1));
      return Number.isInteger(point) &&
        point > 0 &&
        point <= 0x10ffff &&
        !(point >= 0xd800 && point <= 0xdfff)
        ? String.fromCodePoint(point)
        : '\ufffd';
    }
  );
const idAttributePattern = /\sid\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/i;
const attributeId = (value) =>
  String(value)
    .match(idAttributePattern)
    ?.slice(1)
    .find((value) => value !== undefined);
const headingSlug = (value) =>
  articleText(value)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 72);
export function prepareArticleHtml(
  html = '',
  { tableLabel = '', tableHint = '' } = {}
) {
  let body = String(html).replace(
    /<(p|h[1-6])\s*>(?:\s|&nbsp;|&#160;|&#x0*a0;|<br\s*\/?\s*>)*<\/\1>/gi,
    ''
  );
  const reserved = new Set(
    [...body.matchAll(new RegExp(idAttributePattern.source, 'gi'))].map(
      (match) =>
        decodeAttribute(
          match.slice(1).find((value) => value !== undefined) || ''
        )
    )
  );
  const used = new Set();
  const headings = [];
  body = body.replace(
    /<h([23])((?:"[^"]*"|'[^']*'|[^'">])*)>([\s\S]*?)<\/h\1>/gi,
    (match, level, attrs = '', content = '') => {
      const text = articleText(content);
      if (!text) return match;
      const existingRaw = attributeId(attrs);
      const existing = existingRaw ? decodeAttribute(existingRaw) : undefined;
      const base =
        existing || headingSlug(text) || `section-${headings.length + 1}`;
      let id = base;
      let suffix = 1;
      while (used.has(id) || (reserved.has(id) && id !== existing))
        id = `${base}-${++suffix}`;
      used.add(id);
      headings.push({ id, level: Number(level), text });
      const cleanAttrs = String(attrs).replace(idAttributePattern, '');
      return `<h${level}${cleanAttrs} id="${escapeAttribute(id)}">${content}</h${level}>`;
    }
  );
  body = body.replace(
    /<img\b((?:"[^"]*"|'[^']*'|[^'">])*)>/gi,
    (match, attrs) => {
      const additions = [];
      if (!/(?:^|\s)loading\s*=/i.test(attrs)) additions.push('loading="lazy"');
      if (!/(?:^|\s)decoding\s*=/i.test(attrs))
        additions.push('decoding="async"');
      if (!/(?:^|\s)alt\s*=/i.test(attrs)) additions.push('alt=""');
      if (!additions.length) return match;
      const selfClosing = /["'\s]\/\s*$/.test(attrs) || attrs.trim() === '/';
      const clean = selfClosing ? attrs.replace(/\/\s*$/, '') : attrs;
      return `<img${clean} ${additions.join(' ')}${selfClosing ? ' /' : ''}>`;
    }
  );
  // Wrap only complete outer tables; nested tables remain inside their cells.
  const chunks = [];
  let depth = 0;
  let start = 0;
  let last = 0;
  for (const token of body.matchAll(
    /<\/?table\b(?:"[^"]*"|'[^']*'|[^'">])*>/gi
  )) {
    if (!/^<\//.test(token[0])) {
      if (depth === 0) start = token.index;
      depth++;
    } else if (depth > 0) {
      depth--;
      if (depth === 0) {
        const end = token.index + token[0].length;
        const table = body.slice(start, end);
        chunks.push(
          body.slice(last, start),
          `<div class="article-table-shell"><p class="table-hint">${escapeAttribute(tableHint)}</p><div class="article-table" role="region" tabindex="0" aria-label="${escapeAttribute(tableLabel)}">${table}</div></div>`
        );
        last = end;
      }
    }
  }
  body = chunks.join('') + body.slice(last);
  return { body, headings };
}

export function articleWordCount(text, locale = 'en') {
  if (!text) return 0;
  if (typeof Intl.Segmenter === 'function') {
    let count = 0;
    for (const part of new Intl.Segmenter(locale, {
      granularity: 'word'
    }).segment(text))
      if (part.isWordLike) count++;
    return count;
  }
  // Older runtimes without Segmenter still need a useful CJK estimate.
  const cjk = /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]/gu;
  const characters = String(text).match(cjk)?.length || 0;
  const otherWords =
    String(text).replace(cjk, ' ').trim().match(/\S+/g)?.length || 0;
  return otherWords + Math.ceil(characters / 2);
}

// Approximation, not a promised reading duration. Reuse the existing body count.
export function estimatedReadingMinutes(wordCount) {
  return Number.isFinite(wordCount) && wordCount > 0
    ? Math.max(1, Math.ceil(wordCount / 200))
    : 0;
}
